import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || mobileOpen
            ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
              <span className={`font-heading text-xl font-semibold tracking-tight transition-colors duration-300 ${scrolled || mobileOpen ? 'text-foreground-900' : 'text-white'}`}>
                Vigorbuy<span className={scrolled || mobileOpen ? 'text-primary-500' : 'text-accent-300'}>Spreadsheet</span>
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-5">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled || mobileOpen ? 'text-foreground-700 hover:text-primary-500' : 'text-white/80 hover:text-white'}`}>
                Home
              </Link>
              <Link to="/article/vigorbuy-spreadsheet-complete-guide" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled || mobileOpen ? 'text-foreground-700 hover:text-primary-500' : 'text-white/80 hover:text-white'}`}>
                Shopping Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled || mobileOpen ? 'text-foreground-700 hover:text-primary-500' : 'text-white/80 hover:text-white'}`}
              >
                Main Website
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg transition-colors ${scrolled || mobileOpen ? 'text-foreground-900 hover:bg-background-100' : 'text-white hover:bg-white/10'}`}
              aria-label="Menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-background-50 overflow-y-auto">
            <div className="px-4 py-6 space-y-6">
              <div>
                <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Navigation</h3>
                <div className="space-y-1">
                  <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer">
                    <i className="ri-home-4-line text-base"></i>
                    Home
                  </Link>
                  <Link to="/article/vigorbuy-spreadsheet-complete-guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer">
                    <i className="ri-book-open-line text-base"></i>
                    Shopping Guide
                  </Link>
                  <Link to="/article/vigorbuy-spreadsheet-safety-guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer">
                    <i className="ri-shield-check-line text-base"></i>
                    Safety Guide
                  </Link>
                  <Link to="/article/vigorbuy-spreadsheet-category-comparison" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer">
                    <i className="ri-bar-chart-grouped-line text-base"></i>
                    Category Comparison
                  </Link>
                  <Link to="/article/vigorbuy-spreadsheet-beginner-tutorial" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer">
                    <i className="ri-user-star-line text-base"></i>
                    Beginner Tutorial
                  </Link>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer">
                    <i className="ri-external-link-line text-base"></i>
                    Main Website
                  </a>
                </div>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Product Categories</h3>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((cat, index) => (
                    <a
                      key={index}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <div className="w-7 h-7 rounded-md overflow-hidden flex-shrink-0">
                        <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover object-top" />
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-background-200">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-shopping-bag-3-line"></i>
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}